import { Dialog, Transition } from "@headlessui/react";
import { RefreshIcon, XIcon } from "@heroicons/react/outline";
import Color from "color";
import { Field, FormikHelpers, FormikProps } from "formik";
import { find, head } from "lodash";
import React, { Fragment, useCallback, useMemo, useState } from "react";
import * as Yup from "yup";
import DropdownSelect from "../../../../components/forms/DropdownSelect";
import FormProvider from "../../../../components/forms/FormProvider";
import Input from "../../../../components/forms/Input";
import {
  getLabelColorStyle,
  labelColors
} from "../../../../components/labels/utils";
import { useAppearance } from "../../../../context/appearance";
import { useLabels } from "../../../../context/labels";
import { Label } from "../../../../utils/types";

export interface Props {
  label: Label | undefined;
  open: boolean;
  toggleEditLabelForm: (label: Label | undefined) => void;
}

const LabelFormSidebar: React.FC<Props> = ({
  label,
  open,
  toggleEditLabelForm
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { addLabel, updateLabel } = useLabels();
  const { isDark } = useAppearance();

  const closeSidebar = useCallback(
    () => toggleEditLabelForm(undefined),
    [toggleEditLabelForm]
  );

  const initialValues = useMemo(
    () =>
      label
        ? {
            name: label.name,
            color: label.color,
            type: find(typeOptions, { key: label.type })
          }
        : {
            name: "",
            color: head(labelColors),
            type: head(typeOptions)
          },
    [label]
  );

  const onSubmit = useCallback(
    async (
      { name, color, type }: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      setErrorMessage(null);

      const data = { name, color, type: type.key };

      let apiResponse;

      if (!!label) {
        apiResponse = await updateLabel(label.uuid, data);
      } else {
        apiResponse = await addLabel(data);
      }

      if (apiResponse.status) {
        closeSidebar();
      } else {
        setErrorMessage("Something went wrong");
      }

      formikHelpers.setSubmitting(false);
    },
    [label, updateLabel, addLabel, closeSidebar]
  );

  const validateColorField = useCallback((color: string) => {
    if (!color) {
      return "Required";
    }

    try {
      Color(color);
    } catch (e) {
      return "Please provide a valid color";
    }
  }, []);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-20 overflow-hidden"
        open={open}
        onClose={closeSidebar}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div className="fixed inset-y-0 pl-16 max-w-full right-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="w-screen max-w-md">
                <FormProvider
                  classnames={{ wrapper: "h-full" }}
                  onCancel={closeSidebar}
                  error={errorMessage}
                  formikProps={{
                    onSubmit,
                    initialValues,
                    validationSchema: Yup.object().shape({
                      name: Yup.string().required("Required"),
                      color: Yup.string().required("Required"),
                      type: Yup.object()
                        .shape({ key: Yup.string(), name: Yup.string() })
                        .required("Required")
                    })
                  }}
                  orderedFieldNames={ORDERED_FIELD_NAMES}
                >
                  {({
                    isSubmitting,
                    values,
                    errors,
                    setFieldValue
                  }: FormikProps<FormValues>) => {
                    return (
                      <div className="flex-1 h-0 overflow-y-auto">
                        <div className="py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                          <div className="flex items-center justify-between">
                            <Dialog.Title className="text-lg font-medium text-white">
                              {!!label ? "Edit" : "New"} label
                            </Dialog.Title>
                            <div className="ml-3 h-7 flex items-center">
                              <button
                                type="button"
                                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                                onClick={closeSidebar}
                              >
                                <span className="sr-only">Close panel</span>
                                <XIcon className="h-6 w-6" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="flex-1 flex flex-col justify-between">
                          <div className="px-4 divide-y divide-gray-200 dark:divide-gray-500 sm:px-6">
                            <div className="space-y-6 pt-6 pb-5">
                              <Field
                                id="name"
                                name="name"
                                label="Name"
                                placeholder="test label"
                                type="text"
                                component={Input}
                                disabled={isSubmitting}
                              />

                              <Field
                                id="color"
                                name="color"
                                label={
                                  <div className="flex justify-between">
                                    <div>Color</div>
                                    <div
                                      className="flex items-center select-none hover:text-gray-600 truncate dark:hover:text-white dark:hover:underline cursor-pointer font-normal"
                                      onClick={() => {
                                        const colorIndex =
                                          labelColors.findIndex(
                                            (x) => x === values.color
                                          );

                                        if (colorIndex > -1) {
                                          setFieldValue(
                                            "color",
                                            labelColors[
                                              (colorIndex + 1) %
                                                labelColors.length
                                            ]
                                          );
                                        } else {
                                          setFieldValue(
                                            "color",
                                            head(labelColors)
                                          );
                                        }
                                      }}
                                    >
                                      <RefreshIcon className="h-3 w-3 inline mr-1" />
                                      shuffle
                                    </div>
                                  </div>
                                }
                                placeholder="#fff"
                                type="color"
                                styles={{
                                  input: { height: "38px", padding: "0" }
                                }}
                                component={Input}
                                disabled={isSubmitting}
                                validate={validateColorField}
                              />

                              <Field
                                label="Type"
                                name="type"
                                placeholder="Select a type"
                                disabled={isSubmitting || !!label}
                                items={typeOptions}
                                itemToString={(item: TypeItem) => item.name}
                                optionToString={(item: TypeItem) => item.name}
                                component={DropdownSelect}
                              />

                              {values.color && values.name && !errors.color && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-900 dark:text-white">
                                    Preview
                                  </label>
                                  <div
                                    className="inline-flex items-center rounded-full font-medium px-3 py-0.5 my-2 text-sm"
                                    style={getLabelColorStyle(
                                      values.color,
                                      isDark
                                    )}
                                  >
                                    {values.name}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                </FormProvider>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default LabelFormSidebar;

//
// Utils
//

interface TypeItem {
  key: string;
  name: string;
}

const typeOptions: TypeItem[] = [
  "wallet",
  "location",
  "hotspot",
  "contact",
  "install"
].map((type) => ({
  key: type,
  name: type
}));

interface FormValues {
  name: string;
  color: string;
  type: TypeItem;
}

const ORDERED_FIELD_NAMES = ["name", "color", "type"];
